import React, { useEffect, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, ArrowLeftRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DiffResultView } from "@/components/common/DiffResultView";
import { TID } from "@/lib/testIds";

function fmtTime(iso) {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/** Compares the SAME workspace against itself over time — every analysis
 * run automatically takes a snapshot (see backend analyze_workspace ->
 * workspace_snapshots). Reuses the identical diff shape as cross-workspace
 * Compare, so DiffResultView is shared. */
export function TimelineDiffPanel() {
    const [workspaces, setWorkspaces] = useState([]);
    const [wsId, setWsId] = useState("");
    const [snapshots, setSnapshots] = useState([]);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingSnaps, setLoadingSnaps] = useState(false);

    useEffect(() => {
        api.get("/workspaces").then(({ data }) => setWorkspaces(data)).catch(() => {});
    }, []);

    useEffect(() => {
        setResult(null);
        setFrom(""); setTo("");
        if (!wsId) { setSnapshots([]); return; }
        setLoadingSnaps(true);
        api.get(`/workspaces/${wsId}/timeline`).then(({ data }) => {
            setSnapshots(data.snapshots);
            if (data.snapshots.length >= 2) {
                setFrom(data.snapshots[0].id);
                setTo(data.snapshots[data.snapshots.length - 1].id);
            }
        }).catch(() => setSnapshots([])).finally(() => setLoadingSnaps(false));
    }, [wsId]);

    const run = async () => {
        if (!from || !to) { toast.error("Select two snapshots"); return; }
        if (from === to) { toast.error("Pick two different snapshots in time"); return; }
        setLoading(true); setResult(null);
        try {
            const { data } = await api.get(`/workspaces/${wsId}/timeline/diff`, { params: { from, to } });
            setResult(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Timeline diff failed");
        } finally { setLoading(false); }
    };

    return (
        <div className="space-y-6">
            <Panel eyebrow="Timeline Diff" title="Compare a workspace against its own history">
                <p className="text-xs text-slate-500 font-mono mb-3">
                    Every analysis run automatically takes a snapshot. Pick a workspace, then two points in time to see what changed.
                </p>
                <div className="mb-3">
                    <div className="label-eyebrow mb-1.5">Workspace</div>
                    <Select value={wsId} onValueChange={setWsId}>
                        <SelectTrigger data-testid={TID.timelineWorkspaceSelect} className="bg-[var(--bg-surface-2)] border-white/10 text-xs h-9 max-w-md">
                            <SelectValue placeholder="Choose a workspace" />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                            {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                {loadingSnaps ? (
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-mono py-2"><Loader2 size={13} className="animate-spin" /> Loading history…</div>
                ) : wsId && snapshots.length < 2 ? (
                    <p className="text-sm text-amber-400/80 font-mono py-2">
                        {snapshots.length === 0 ? "No analysis runs yet for this workspace." : "Only one snapshot so far — run analysis again after making changes to compare over time."}
                    </p>
                ) : wsId ? (
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
                        <div>
                            <div className="label-eyebrow mb-1.5">From (earlier)</div>
                            <Select value={from} onValueChange={setFrom}>
                                <SelectTrigger data-testid={TID.timelineFromSelect} className="bg-[var(--bg-surface-2)] border-white/10 text-xs h-9">
                                    <SelectValue placeholder="Snapshot" />
                                </SelectTrigger>
                                <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                    {snapshots.map((s) => <SelectItem key={s.id} value={s.id}>{fmtTime(s.taken_at)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="text-cyan-400 px-2 hidden md:block"><History size={16} /></div>
                        <div>
                            <div className="label-eyebrow mb-1.5">To (later)</div>
                            <Select value={to} onValueChange={setTo}>
                                <SelectTrigger data-testid={TID.timelineToSelect} className="bg-[var(--bg-surface-2)] border-white/10 text-xs h-9">
                                    <SelectValue placeholder="Snapshot" />
                                </SelectTrigger>
                                <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                    {snapshots.map((s) => <SelectItem key={s.id} value={s.id}>{fmtTime(s.taken_at)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <button
                            data-testid={TID.timelineRunBtn}
                            onClick={run}
                            disabled={loading || !from || !to || from === to}
                            className="btn-primary flex items-center gap-2 h-9 disabled:opacity-50"
                        >
                            {loading ? <Loader2 size={12} className="animate-spin" /> : <ArrowLeftRight size={12} />}
                            {loading ? "Comparing…" : "Run Diff"}
                        </button>
                    </div>
                ) : null}
            </Panel>

            <DiffResultView result={result} labelA="Earlier" labelB="Later" />
        </div>
    );
}
